/**
 * Sample data for the visual shell, remapped to match v0.6 spec terminology.
 * Replaced with real API responses once backend endpoints land.
 *
 * Spec reference: hub.coastalai.dev/s/d1447c0a09710002bdb67574ac22e01fb2cdd674a35c03c019dc3d90cc6b5db2
 */

export type CaseState = 'NEW' | 'SURFACED' | 'NOTIFIED' | 'ACKD' | 'CONFIRMED_VIOLATION' | 'RESOLVED' | 'DISMISSED' | 'CLEARED_BY_EXCEPTION';
export type FlagSeverity = 'danger' | 'warn' | 'gold' | 'ok' | 'info';
export type FlagKind =
  | 'violation_candidate'    // bottom of tree, awaiting human confirmation
  | 'split_care'             // Q1: <85% dominant zip
  | 'high_manual_rate'       // QM: >50% manual clock-ins
  | 'cross_zip_mismatch'     // QXZ: dominant zip != record zip + one outside territory
  | 'break_in_service'       // Q4b: >=30 day gap voids grandfather
  | 'address_tampering'      // street address geocodes to a different zip than listed
  | 'geocode_tampering'      // stored AxisCare geocode != street address geocode
  | 'zip_mismatch'           // record zip != dominant clock-in zip (data hygiene)
  | 'spc_scope_review'       // Q5: a payer-scoped SPC/IR may apply but couldn't be verified
  | 'placeholder_client'     // QN: client name looks like an office / training / caregiver record
  | 'client_status_unknown'; // no AxisCare active/inactive flag, audited anyway

export type PayerCode = 'va' | 'medicaid' | 'ltci' | 'private' | 'medicare';

export interface FlagType {
  label: string;
  sev: FlagSeverity;
  desc: string;
  ruleId: string;
}

export const FLAG_TYPES: Record<FlagKind, FlagType> = {
  violation_candidate: {
    label: 'Violation candidate',
    sev: 'danger',
    desc: 'All auto-tree gates indicate encroachment. Awaiting human confirmation per Q10.',
    ruleId: 'tree.terminal',
  },
  split_care: {
    label: 'Split care',
    // Caution, not warn: a patient below the 85% dominant-zip threshold is
    // usually splitting time legitimately across territories, so this belongs
    // with the data-hygiene tier rather than in Priority alongside violation
    // candidates. Per Bettina 2026-08-24.
    sev: 'gold',
    desc: 'No dominant zip ≥85% in the audit window. Client may legitimately split time across territories.',
    ruleId: 'Q1.dominant_zip',
  },
  high_manual_rate: {
    label: 'High manual rate',
    sev: 'gold',
    desc: '>50% of clock-ins entered manually by office staff (no GPS). Possible masking pattern.',
    ruleId: 'QM.manual_rate',
  },
  cross_zip_mismatch: {
    label: 'Cross-zip mismatch',
    sev: 'warn',
    desc: 'Dominant zip and client record zip disagree, with at least one outside the franchisee’s territory.',
    ruleId: 'QXZ.cross_zip',
  },
  break_in_service: {
    label: 'Break in service',
    sev: 'warn',
    desc: '≥30-day gap in visit history after the territory owner’s effective date. Grandfather may not apply.',
    ruleId: 'Q4b.break_in_service',
  },
  address_tampering: {
    label: 'Zip mismatch',
    sev: 'danger',
    desc: 'Client’s street address geocodes to a different zip than the listed zip on the AxisCare record.',
    ruleId: 'data_hygiene.address_tampering',
  },
  geocode_tampering: {
    label: 'Geocode tampering',
    sev: 'danger',
    desc: 'Stored AxisCare geocode does not match the geocode of the listed street address. Manually-edited coords.',
    ruleId: 'data_hygiene.geocode_tampering',
  },
  zip_mismatch: {
    label: 'ZIP mismatch',
    sev: 'gold',
    desc: 'Client record zip differs from the dominant clock-in zip. Often a benign border-of-zip artifact.',
    ruleId: 'data_hygiene.zip_mismatch',
  },
  spc_scope_review: {
    label: 'SPC needs review',
    // Warn, not gold: this asks a reviewer to verify whether a permission
    // contract actually applies — a higher-stakes question than the gold-tier
    // data-hygiene flags it used to sit beside (high manual rate, zip
    // mismatch). Per Bettina 2026-07-31.
    sev: 'warn',
    desc: 'A payer-scoped SPC/IR from the territory owner may cover this pair, but the visit payer mix is missing or only partially matches — verify the grant manually.',
    ruleId: 'Q5.spc_review',
  },
  placeholder_client: {
    label: 'Possible non-client',
    sev: 'warn',
    desc: 'The client name contains a non-client keyword (Exception ledger → Non-clients) — possibly an office, staff, training, orientation or caregiver record rather than a real client. Mark it "Not a real client" to close it and stop future alerts for this servicing franchisee.',
    ruleId: 'QN.placeholder_name',
  },
  client_status_unknown: {
    label: 'Client status unknown',
    sev: 'warn',
    desc: 'Audits only cover clients active in AxisCare, but the warehouse has no active/inactive flag for this client, so it was audited anyway. Check the client in AxisCare.',
    ruleId: 'run.client_active',
  },
};

export const PAYERS: { id: PayerCode; label: string; color: string }[] = [
  { id: 'va',       label: 'VA',          color: 'oklch(0.50 0.10 240)' },
  { id: 'medicaid', label: 'Medicaid',    color: 'oklch(0.55 0.12 25)' },
  { id: 'ltci',     label: 'LTCi',        color: 'oklch(0.55 0.10 160)' },
  { id: 'private',  label: 'Private pay', color: 'oklch(0.55 0.05 230)' },
  { id: 'medicare', label: 'Medicare',    color: 'oklch(0.55 0.10 280)' },
];

// Colours for a case's payer split, by the payer's rank in that split (which
// is ordered largest share first). They only tell payers apart within one
// case; they carry no VA / Medicaid / Private meaning.
const PAYER_PALETTE = [
  'oklch(0.50 0.10 240)', 'oklch(0.55 0.12 25)', 'oklch(0.55 0.10 160)',
  'oklch(0.55 0.10 280)', 'oklch(0.62 0.12 75)', 'oklch(0.55 0.08 200)',
  'oklch(0.55 0.10 330)', 'oklch(0.60 0.10 120)',
];
export function payerColor(split: Record<string, number>, name: string): string {
  const i = Object.keys(split).indexOf((name ?? '').trim() || '(no payer recorded)');
  return i < 0 ? 'oklch(0.7 0.02 240)' : PAYER_PALETTE[i % PAYER_PALETTE.length];
}

export const STATE_META: Record<CaseState, { label: string; dot: FlagSeverity }> = {
  NEW:       { label: 'New',         dot: 'info' },
  // SURFACED is shown as "Reviewed": ready to notify, NOT a confirmed
  // violation (decision 0006). The stored value stays SURFACED.
  SURFACED:  { label: 'Reviewed',    dot: 'warn' },
  NOTIFIED:  { label: 'Notified',    dot: 'danger' },
  ACKD:      { label: 'Acknowledged', dot: 'gold' },
  // Set after the partner responds there is no arrangement (decision 0006).
  CONFIRMED_VIOLATION: { label: 'Confirmed violation', dot: 'danger' },
  RESOLVED:  { label: 'Resolved',    dot: 'ok' },
  DISMISSED: { label: 'Dismissed',   dot: 'info' },
  // An SPC or Internal Referral answered this claim. Terminal, but not frozen:
  // if the grant lapses the audit walks the case back to NEW.
  CLEARED_BY_EXCEPTION: { label: 'Cleared by SPC/IR/FA', dot: 'ok' },
};

export interface Case {
  id: string;
  patient: string;
  /** AxisCare client status (Active / Inactive / …); null when not synced yet. */
  clientStatus: string | null;
  // AxisCare's active switch (warehouse); null = unknown. The label above is
  // free text per franchise ("Route 1" is active, "On Hold" is not).
  clientActive: boolean | null;
  // Grandfathered (Q4): start of care predates the zip owner's effective date
  // and no break in service voided it. Shown as a green "Legacy" pill.
  legacy: { reason: string } | null;
  axiscareClientId: string;
  location: string;             // city, state from AxisCare client record
  franchiseeAf: string;         // e.g. "AF-219" — the servicing franchisee
  franchiseeName: string;
  ownerAf: string | null;       // territory owner of dominant_zip; null if unowned
  ownerName: string | null;
  /** Owner's Zoho status, and whether it is a not-yet-open territory (Pending /
   *  Pending-Licensure / Deferral) that owns its zips under a pre-2026 FDD. */
  ownerStatus?: string | null;
  ownerNotYetOpen?: boolean;
  ownerFddEra?: 'pre2026' | 'fdd2026' | 'unknown' | null;
  /** FBC (franchise business consultant) assigned in Zoho — Matt Smith's org
   * maintains the assignment; synced with the franchisee record. */
  servicingFbcName: string | null;
  servicingFbcEmail: string | null;
  ownerFbcName: string | null;
  ownerFbcEmail: string | null;
  dominantZip: string;          // Z_eval
  zipOnlyTag: boolean;
  manualRate: number;           // 0..1
  longestGapDays: number | null;  // longest gap (days) between consecutive visits in window
  flags: FlagKind[];
  /** Real per-flag detail payloads from the audit engine, keyed by flag kind
   * (e.g. cross_zip_mismatch → { dominantZip, recordZip }). Optional so demo
   * data and older cases without stored details still render. */
  flagDetails?: Partial<Record<FlagKind, Record<string, unknown>>>;
  sev: FlagSeverity;
  state: CaseState;
  totalRevenueCents: number;
  payerSplit: Record<string, number>;                 // AxisCare payer name → % of revenue, largest first
  payerBreakdown: PayerBreakdownEntry[];              // per-payer × per-zip detail (raw AxisCare names)
  visits: number;
  caregivers: number;
  hours: number;
  // Null when not on file — shown as "—", never filled with another date.
  startOfCare: Date | null;
  ownerEffectiveDate: Date | null;     // the date the tree's Q4 used
  ownerEffectiveDateSource: string | null; // which Zoho field it came from
  daysGrandfather: number | null;      // days between start_of_care and effective_date (negative = grandfathered)
  // Null means "the engine didn't record this" — render an em dash, never a
  // plausible default (a fabricated "0.0s runtime" / "v0.6.0" / "system" reads
  // as real data to a legal reviewer).
  audit: {
    runId: string | null;
    specVersion: string | null;
    runtimeMs: number | null;
    triggeredBy: string | null;
    triggeredAt: Date;
  };
  assignee: { id: number; name: string | null; email: string | null } | null;
  openedAt: Date;
  /** When the case's latest audit run ran (the Case queue timeframe filter). */
  auditRunAt: Date | null;
}

export interface PayerBreakdownEntry {
  payer: string;        // raw AxisCare payer name (e.g. "VA", "Medicaid", "Private Pay")
  zip: string;
  visits: number;
  minutes: number;
  revenueCents: number;
}

// NOTE: the demo-era fabricated dataset (PATIENTS/FRANCHISEES/ZIPS,
// buildCases(), ENABLED_STATES_OPTIONS) was removed 2026-07-09 — it was dead
// code, but one accidental import would have silently repopulated the console
// with invented cases. This module now holds only types, display constants,
// and formatters. (git history has the generators if ever needed for a demo.)

/**
 * A date-only value from the API ("2026-06-30" or "2026-06-30T00:00:00.000Z")
 * as that calendar day at LOCAL midnight. `new Date(iso)` reads it as UTC
 * midnight, which every US time zone shows as the day before (start of care
 * 6/30 displayed as 6/29, case 5543). Use for DATE columns only, never for
 * timestamps.
 */
export function parseDateOnly(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof v === 'string' ? v : v.toISOString());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * "Run #97 - " / "Run #95, 96, 97 - " from the case export's Content-Disposition
 * (the server knows each case's run). Export file names start with it
 * (Bettina, 2026-10-21); "" when no exported case has a run.
 */
export function runPrefixFrom(res: Response): string {
  const m = /filename="(Run #[^"]*? - )/.exec(res.headers.get('Content-Disposition') ?? '');
  return m ? m[1] : '';
}

/** Local calendar day as YYYY-MM-DD (the inverse of parseDateOnly). */
export function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatRelative(d: Date): string {
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCentsShort(cents: number): string {
  const v = cents / 100;
  if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'k';
  return '$' + v.toFixed(0);
}
